import { apiClient } from '@/lib/api-client';
import i18n from 'i18next';

export interface StatusLogItem {
  Id: number;
  SourceAppId: string;
  SourceSubId?: string;
  Type: string;
  Status: string;
  Processed?: string; // Raw date from API (may be .NET format or ISO)
  FormattedProcessedDate?: string; // Formatted for display
  UserId: string;
  Message?: string;
  ErrorMessage?: string;
  SubmissionType?: string;
  ApplicationType?: string;
  Path?: string;
  QueueTime?: string;
  StartTime?: string;
  ErrorStack?: string;
  CompanyName?: string; // Populated only in all-companies mode
}

export interface StatusResponse {
  Total: number;
  Results: StatusLogItem[];
}

export interface StatusParams {
  companyId: number;
  startDate: string;
  endDate: string;
  currentUserOnly: boolean;
  appFilter?: string;
  startIndex: number;
  endIndex: number;
  userFilter?: string;
  sort?: string;
  allCompanies?: boolean;
}

class StatusService {
  /**
   * Format a processed date from the API to a display string
   * Handles .NET JSON date format (/Date(timestamp)/) and ISO strings
   */
  private formatProcessedDate(processed?: string): string {
    if (!processed) return '';

    let timestamp: number | null = null;

    // Handle .NET JSON date format: /Date(1234567890000)/
    const dotNetMatch = processed.match(/\/Date\((\d+)\)\//);
    if (dotNetMatch) {
      timestamp = parseInt(dotNetMatch[1], 10);
    } else {
      // Try parsing as ISO date or other standard format
      const date = new Date(processed);
      if (!isNaN(date.getTime())) {
        timestamp = date.getTime();
      }
    }

    if (timestamp) {
      const date = new Date(timestamp);
      // Format like Vue app: "Tue, 1/13/2026, 2:24:24 AM"
      return new Intl.DateTimeFormat(i18n.language, {
        weekday: 'short',
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        second: 'numeric',
        hour12: true,
      }).format(date);
    }

    return processed; // Return as-is if we can't parse it
  }

  /**
   * Get status/activity logs for a company with filters
   */
  async getStatus(params: StatusParams): Promise<StatusResponse> {
    const queryParams = new URLSearchParams({
      companyId: params.companyId.toString(),
      startDate: params.startDate,
      endDate: params.endDate,
      currentUserOnly: params.currentUserOnly.toString(),
      appFilter: params.appFilter || '',
      startIndex: params.startIndex.toString(),
      endIndex: params.endIndex.toString(),
      userFilter: params.userFilter || '',
      sort: params.sort || '',
      allCompanies: (params.allCompanies ?? false).toString(),
    });

    const result = await apiClient.get<StatusResponse>(
      `api/Status/GetStatus?${queryParams}`
    );

    if (result && result.Results) {
      // Format the processed dates for display
      result.Results = result.Results.map(item => ({
        ...item,
        FormattedProcessedDate: this.formatProcessedDate(item.Processed),
      }));
    }

    return result || { Total: 0, Results: [] };
  }

  /**
   * Get detailed log information by ID
   */
  async getLogDetails(companyId: number, logId: number) {
    const queryParams = new URLSearchParams({
      companyId: companyId.toString(),
      logId: logId.toString(),
    });

    return apiClient.get(`api/Status/GetLogDetails?${queryParams}`);
  }

  /**
   * Validates that a Date object is valid
   */
  private isValidDate(date: Date): boolean {
    return date instanceof Date && !isNaN(date.getTime());
  }

  /**
   * Format a date to the format expected by backend: "YYYY-MM-DD HH:mm:ss"
   * Backend parses using delimiters: '-', ' ', ':'
   */
  formatDateForApi(date: Date | string): string {
    const d = typeof date === 'string' ? new Date(date) : date;
    if (!this.isValidDate(d)) {
      throw new Error(`Invalid date provided: ${date}`);
    }
    // Format: YYYY-MM-DD HH:mm:ss (no milliseconds, no Z, space instead of T)
    return d.toISOString().replace('T', ' ').replace(/\.\d{3}Z$/, '');
  }

  /**
   * Get end of day for a date (next day at midnight UTC)
   * Returns in format expected by backend
   */
  getDayEnd(date: Date | string): string {
    const d = typeof date === 'string' ? new Date(date) : new Date(date);
    if (!this.isValidDate(d)) {
      throw new Error(`Invalid date provided: ${date}`);
    }
    d.setUTCDate(d.getUTCDate() + 1);
    d.setUTCHours(0, 0, 0, 0);
    // Format: YYYY-MM-DD HH:mm:ss (no milliseconds, no Z, space instead of T)
    return d.toISOString().replace('T', ' ').replace(/\.\d{3}Z$/, '');
  }

  /**
   * Export status logs as a CSV file download.
   * Uses apiClient.get to include the Authorization header, then triggers a
   * browser download from the returned blob.
   */
  async exportCsv(params: {
    companyId: number;
    startDate: string;
    endDate: string;
    currentUserOnly: boolean;
    appFilter: string;
    userFilter: string;
    allCompanies?: boolean;
  }): Promise<void> {
    const queryParams = new URLSearchParams({
      companyId: params.companyId.toString(),
      startDate: params.startDate,
      endDate: params.endDate,
      currentUserOnly: params.currentUserOnly.toString(),
      appFilter: params.appFilter,
      userFilter: params.userFilter,
      allCompanies: (params.allCompanies ?? false).toString(),
    });

    const blob = await apiClient.getBlob(`api/Status/ExportCsv?${queryParams}`);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `load-activity-export-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }
}

export const statusService = new StatusService();

